import { Button } from '@alicerce/ui';
import { Upload } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { ApiError, errorMessage, uploadToSignedUrl } from '@/lib/api';

export interface Presigned {
  url: string;
  key: string;
  headers?: Record<string, string>;
}

/**
 * Upload direto ao S3: pede a URL pré-assinada à API, envia o arquivo e devolve a chave para confirmar.
 * Valida tipo e tamanho antes de pedir a URL, com mensagem em português.
 */
export function useUpload(presign: (file: File) => Promise<Presigned>, opts: { maxBytes: number; accept: (type: string) => boolean; typeMessage: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function upload(file: File): Promise<string | null> {
    setError(null);
    if (!opts.accept(file.type)) {
      setError(opts.typeMessage);
      return null;
    }
    if (file.size > opts.maxBytes) {
      setError(`O arquivo pode ter no máximo ${Math.round(opts.maxBytes / 1024 / 1024)} MB.`);
      return null;
    }
    setBusy(true);
    try {
      const p = await presign(file);
      await uploadToSignedUrl(p.url, file, p.headers?.['Content-Type']);
      return p.key;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : errorMessage(e));
      return null;
    } finally {
      setBusy(false);
    }
  }
  return { upload, busy, error, setError };
}

export function FilePicker({ accept, multiple, onFiles, busy, children }: { accept: string; multiple?: boolean; onFiles: (files: File[]) => void; busy?: boolean; children?: ReactNode }) {
  const ref = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onFiles(Array.from(e.dataTransfer.files));
      }}
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center text-sm ${over ? 'border-brand-500 bg-brand-50' : 'border-line'}`}
    >
      <Upload className="size-5 text-muted" aria-hidden />
      <p className="text-muted">{children ?? 'Arraste o arquivo aqui ou'}</p>
      <Button size="sm" variant="secondary" loading={busy} onClick={() => ref.current?.click()}>
        Escolher arquivo
      </Button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        aria-label="Escolher arquivo"
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
    </div>
  );
}
